import * as THREE from 'three';

/**
 * Trail类 - 粒子拖尾效果
 * 
 * 职责:
 * - 创建轨迹点位置数组
 * - 使用BufferGeometry和LineBasicMaterial渲染拖尾
 * - 实现addPoint和update方法
 * - 实现dispose清理方法
 * 
 * 验证需求: Requirements 5.1, 5.2, 5.5
 */
class Trail {
  constructor(maxLength = 20, color = 0xffffff, opacity = 0.8) {
    this.maxLength = maxLength;
    this.positions = [];
    
    // 创建几何体
    this.geometry = new THREE.BufferGeometry();
    
    // 创建材质
    this.material = new THREE.LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
      blending: THREE.AdditiveBlending
    });
    
    // 创建线条网格
    this.mesh = new THREE.Line(this.geometry, this.material);
    
    // 初始化位置数组
    this._initializePositions();
  }

  /**
   * 初始化位置数组
   * @private
   */
  _initializePositions() {
    // 创建空的位置数组
    const positions = new Float32Array(this.maxLength * 3);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setDrawRange(0, 0);
  }

  /**
   * 添加一个轨迹点
   * @param {THREE.Vector3} position - 新的位置点
   */
  addPoint(position) {
    // 添加新位置到数组开头
    this.positions.unshift(position.clone());
    
    // 限制数组长度
    if (this.positions.length > this.maxLength) {
      this.positions.pop();
    }
    
    // 更新几何体
    this._updateGeometry();
  }

  /**
   * 更新几何体
   * @private
   */
  _updateGeometry() {
    const positionAttribute = this.geometry.getAttribute('position');
    
    // 更新位置数据
    for (let i = 0; i < this.positions.length; i++) {
      const pos = this.positions[i];
      positionAttribute.setXYZ(i, pos.x, pos.y, pos.z);
    }
    
    // 标记需要更新
    positionAttribute.needsUpdate = true;
    
    // 设置绘制范围
    this.geometry.setDrawRange(0, this.positions.length);
  }

  /**
   * 更新拖尾（每帧调用）
   * @param {THREE.Vector3} currentPosition - 当前粒子位置
   * @param {number} speed - 当前粒子速度
   */
  update(currentPosition, speed) {
    // 添加当前位置
    this.addPoint(currentPosition);
    
    // 根据速度调整不透明度
    // 速度越大，拖尾越明显
    const speedFactor = Math.min(speed / 15, 1.0);
    this.material.opacity = 0.3 + speedFactor * 0.5;
  }

  /**
   * 设置拖尾颜色
   * @param {THREE.Color|number} color - 颜色
   */
  setColor(color) {
    if (color instanceof THREE.Color) {
      this.material.color.copy(color);
    } else {
      this.material.color.set(color);
    }
  }

  /**
   * 设置拖尾不透明度
   * @param {number} opacity - 不透明度 (0-1)
   */
  setOpacity(opacity) {
    this.material.opacity = Math.max(0, Math.min(1, opacity));
  }

  /**
   * 获取拖尾长度
   * @returns {number}
   */
  getLength() {
    return this.positions.length;
  }

  /**
   * 清空拖尾
   */
  clear() {
    this.positions = [];
    this.geometry.setDrawRange(0, 0);
  }

  /**
   * 清理资源
   */
  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    this.positions = [];
  }

  /**
   * 获取网格对象（用于添加到场景）
   * @returns {THREE.Line}
   */
  getMesh() {
    return this.mesh;
  }
}

export default Trail;
